import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { validateNoBadWords } from '../utils/badWords';
import { userHasActiveSupportTicket } from '../utils/supportTicketLimits';
import { userCannotSendMoreMessages, userMaySendFollowUp } from '../utils/supportTicketStatus';
import { deleteSupportTicketCascade } from '../utils/deleteSupportTicket';
import Spinner from './Spinner';
import ConfirmModal from './ui/ConfirmModal';

const BlockedUserScreen = ({ user }) => {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const primaryTicket = useMemo(() => {
    const active = tickets.find((t) => ['open', 'in_progress'].includes(t.status));
    return active || tickets[0];
  }, [tickets]);
  const selectedId = primaryTicket?.id;
  const [messages, setMessages] = useState([]);
  const ticketClosed = userCannotSendMoreMessages(primaryTicket?.status);
  const canSendFollowUp = userMaySendFollowUp(primaryTicket?.status, messages);
  const [replyBody, setReplyBody] = useState('');
  const [newSubject, setNewSubject] = useState('Blocked account — contact admin');
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'supportTickets'),
      where('authorUid', '==', user.uid),
      orderBy('updatedAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTicketsLoading(false);
      },
      () => setTicketsLoading(false),
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!selectedId || !user) {
      setMessages([]);
      return undefined;
    }
    const q = query(
      collection(db, 'supportTickets', selectedId, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedId, user]);

  const sendFollowUp = async () => {
    const body = replyBody.trim();
    if (!body || !selectedId || !user) return;
    if (ticketClosed) {
      setError('This request is closed. You cannot send more messages.');
      return;
    }
    if (!userMaySendFollowUp(primaryTicket?.status, messages)) {
      setError('Wait for the team to reply before sending another message.');
      return;
    }
    const profanity = validateNoBadWords(body);
    if (profanity) {
      setError(profanity);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await addDoc(collection(db, 'supportTickets', selectedId, 'messages'), {
        body,
        authorRole: 'user',
        authorUid: user.uid,
        createdAt: new Date().toISOString(),
      });
      await updateDoc(doc(db, 'supportTickets', selectedId), {
        updatedAt: new Date().toISOString(),
      });
      setReplyBody('');
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewTicket = async () => {
    if (!user) return;
    const cleanSubject = newSubject.trim().slice(0, 200);
    const cleanMessage = newMessage.trim();
    if (!cleanSubject || !cleanMessage) {
      setError('Please enter a subject and message.');
      return;
    }
    const profanity = validateNoBadWords(cleanSubject, cleanMessage);
    if (profanity) {
      setError(profanity);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const busy = await userHasActiveSupportTicket(user.uid);
      if (busy) {
        setError('You already have an open request. Please wait for a reply.');
        setSubmitting(false);
        return;
      }
      const now = new Date().toISOString();
      const ticketRef = await addDoc(collection(db, 'supportTickets'), {
        subject: cleanSubject,
        category: 'blocked_contact',
        status: 'open',
        authorUid: user.uid,
        authorEmail: user.email || '',
        authorName: user.displayName || user.email?.split('@')[0] || 'User',
        source: 'blocked_screen',
        createdAt: now,
        updatedAt: now,
      });
      await addDoc(collection(db, 'supportTickets', ticketRef.id, 'messages'), {
        body: cleanMessage,
        authorRole: 'user',
        authorUid: user.uid,
        createdAt: now,
      });
      setNewMessage('');
    } catch {
      setError('Could not send. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const executeDeletePrimaryTicket = async () => {
    if (!selectedId) return;
    setDeleteBusy(true);
    setError('');
    const id = selectedId;
    try {
      await deleteSupportTicketCascade(db, id);
      setTickets((prev) => prev.filter((t) => t.id !== id));
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(err?.message || 'Could not delete.');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (ticketsLoading) {
    return (
      <div className="container mt-5 text-center py-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: 560 }}>
      <ConfirmModal
        show={showDeleteConfirm}
        title="Delete this request?"
        titleIcon="bi-trash3"
        confirmText="Delete permanently"
        confirmVariant="danger"
        pending={deleteBusy}
        onCancel={() => {
          if (deleteBusy) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={executeDeletePrimaryTicket}
      >
        <p className="mb-0 small" style={{ lineHeight: 1.5 }}>
          This removes the request and its messages. This cannot be undone.
        </p>
      </ConfirmModal>

      <div
        className="card border-0 shadow-lg mx-auto"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--glass-border)' }}
      >
        <div className="card-body p-4 text-center">
          <div className="rounded-circle bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 56, height: 56 }}>
            <i className="bi bi-slash-circle fs-4" aria-hidden />
          </div>
          <h1 className="h4 mb-2">Account restricted</h1>
          <p className="text-secondary small mb-4">
            Your account is blocked. Please contact admin using the message below. That is the only action available until an admin responds.
          </p>
          <button type="button" className="btn btn-outline-secondary btn-sm mb-4" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>

      {selectedId ? (
        <div className="card border-0 shadow-sm mt-4" style={{ background: 'var(--card-bg)' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h2 className="h6 mb-0">Your request</h2>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={deleteBusy}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            </div>
            {ticketClosed ? (
              <p className="small text-secondary mb-3">This request is closed. You cannot send more messages.</p>
            ) : null}
            {!ticketClosed && messages.length === 0 ? (
              <p className="small text-secondary mb-3">Loading messages…</p>
            ) : null}
            {!ticketClosed && messages.length > 0 && !canSendFollowUp ? (
              <p className="small text-secondary mb-3">
                You have sent a message. Wait for the team to reply before sending another one.
              </p>
            ) : null}
            <div className="d-flex flex-column gap-2 mb-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className="p-3 rounded small text-start"
                  style={{
                    background:
                      m.authorRole === 'admin'
                        ? 'rgba(13,110,253,0.12)'
                        : 'var(--glass-bg)',
                    border:
                      m.authorRole === 'admin'
                        ? 'none'
                        : '1px solid var(--glass-border)',
                  }}
                >
                  <div className="text-muted mb-1">{m.authorRole}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                </div>
              ))}
            </div>
            {canSendFollowUp ? (
              <>
                <textarea
                  className="form-control mb-2"
                  rows={4}
                  placeholder="Follow-up message…"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={submitting || !replyBody.trim()}
                  onClick={sendFollowUp}
                >
                  Send update
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm mt-4" style={{ background: 'var(--card-bg)' }}>
          <div className="card-body">
            <label className="form-label small">Subject</label>
            <input
              type="text"
              className="form-control mb-3"
              maxLength={200}
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
            />
            <label className="form-label small">Message</label>
            <textarea
              className="form-control mb-3"
              rows={6}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Explain briefly — admin will reply here."
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting || !newMessage.trim()}
              onClick={submitNewTicket}
            >
              Send to admin
            </button>
          </div>
        </div>
      )}

      {error ? (
        <div className="alert alert-danger mt-3 small mb-0" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default BlockedUserScreen;
